import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import TextField from "@mui/material/TextField";
import { common, grey, red } from "@mui/material/colors";
import { lighten } from "@mui/material/styles";
import { whyFlaggedCriteria } from "./why-flagged";

import type { ChangeEvent, FC } from "react";
import type {
  MalnutritionCriterion,
  MalnutritionFeedback,
  MalnutritionFeedbackCriterion,
  MalnutritionFeedbackVariables,
  MalnutritionStatus,
} from "../../types/Malnutrition";

const MAX_COMMENT_LENGTH = 1000;
// The counter appears once the user is within 100 characters of the cap, and
// stays visible from then on rather than flickering back out as they delete.
const COMMENT_COUNTER_THRESHOLD = 900;
const TEXTAREA_MIN_ROWS = 2;
const TEXTAREA_MAX_ROWS = 6;

const SUBMIT_HINT = "Select a criterion or add a comment.";

// Red on purpose, per the design: the panel follows up a "No", so it carries the
// No's red. It draws from MUI's red directly rather than malnutrition-colors,
// which (ENG-30844) keeps red for clinical decline in the patient's data — the
// same reason the Yes/No pills use MUI's green/red directly.
//
// red[800] rather than red[700]: on the tint below, the heading clears 4.5:1
// (4.96) and white-on-fill clears it as the Submit background (5.6), where
// red[700] only just scrapes 4.5. It is also exactly the design's red, once
// its screenshot is converted from the display profile to sRGB.
const FEEDBACK_RED = red[800];
// #FDEDED — MUI's error Alert surface.
const FEEDBACK_TINT = lighten(red[400], 0.9);

// The panel is effectively a singleton per card, but ids are generated anyway so
// two cards on one page can't collide on the group label / hint wiring.
let panelInstanceCount = 0;

const styles = {
  card: css`
    background: ${FEEDBACK_TINT};
    margin: 0 0.75rem 0.75rem;
    padding: 0.875rem;
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  `,
  headingRow: css`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.375rem;
  `,
  // Buttons wrap instead of clipping at the narrowest supported card width.
  actions: css`
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.5rem;
  `,
  visuallyHidden: css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  `,
};

const sxStyles = {
  heading: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: FEEDBACK_RED,
  },
  subheading: {
    fontSize: "0.75rem",
    color: grey[700],
  },
  emptyChecklist: {
    fontSize: "0.8125rem",
    color: grey[700],
  },
  criterionRow: {
    alignItems: "flex-start",
    marginLeft: 0,
    marginRight: 0,
    "& .MuiFormControlLabel-label": {
      fontSize: "0.875rem",
      color: grey[900],
      lineHeight: 1.4,
      // Long labels wrap rather than truncate; the checkbox stays level with the
      // first line.
      paddingTop: "0.375rem",
      overflowWrap: "anywhere",
    },
  },
  checkbox: {
    paddingTop: "0.25rem",
    paddingBottom: 0,
    // Unchecked is MUI's default (translucent black), as in the design. The
    // design shows no checked box, so checked keeps the panel's red.
    "&.Mui-checked": { color: FEEDBACK_RED },
  },
  // 30px between rows, as in the design.
  criteria: { gap: "0.25rem" },
  comment: {
    "& .MuiInputBase-input": { fontSize: "0.875rem" },
    "& .MuiInputBase-input::placeholder": { color: grey[700], opacity: 1 },
    "& .MuiOutlinedInput-root": {
      backgroundColor: common.white,
      "& .MuiOutlinedInput-notchedOutline": { borderColor: grey[700] },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: grey[900] },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: FEEDBACK_RED,
      },
    },
  },
  counter: {
    alignSelf: "flex-end",
    fontSize: "0.75rem",
    color: grey[700],
  },
  // Both buttons in the design are 4px-radius rectangles casting the same
  // shadow, whose falloff matches MUI's standard button elevation (shadows[2]).
  buttonBase: {
    textTransform: "none",
    borderRadius: "0.25rem",
    minWidth: "4rem",
    height: "1.875rem",
    fontWeight: 600,
    boxShadow: 2,
    "&:hover": { boxShadow: 2 },
  },
  skipButton: {
    // grey[900] text on a grey[700] border, as in the design. (The Yes/No pills'
    // lighter grey[400] border would drop under the 3:1 non-text contrast floor
    // on this pink surface.)
    color: grey[900],
    borderColor: grey[700],
    backgroundColor: common.white,
    "&:hover": {
      borderColor: grey[900],
      backgroundColor: grey[100],
      boxShadow: 2,
    },
  },
  submitButton: {
    color: common.white,
    backgroundColor: FEEDBACK_RED,
    "&:hover": { backgroundColor: red[900], boxShadow: 2 },
    // Disabled is carried by fill and weight, not just a tint, and still clears
    // 4.5:1 so the label stays readable.
    "&.Mui-disabled": { backgroundColor: grey[300], color: grey[700] },
  },
};

// Every criterion goes on the wire, blank `fact` included. The endpoint accepts
// a blank one rather than rejecting an otherwise valid answer over an identifier
// the server itself coalesced — and dropping one here would shorten
// presented_criteria, which is the denominator a rejection rate is counted
// against.
const toWireCriteria = (
  criteria: MalnutritionCriterion[]
): MalnutritionFeedbackCriterion[] =>
  criteria.map((criterion) => ({
    fact: criterion.fact,
    // The label the user actually read on the card.
    display_value: criterion.title,
  }));

interface CriteriaFeedbackPanelProps {
  emrPatientID: string;
  emrHospitalID: string;
  criteria: MalnutritionCriterion[];
  status: MalnutritionStatus | null;
  // The answer already on record for this patient, which the panel opens on so
  // the user edits it rather than being asked again. Shared, so it may be a
  // colleague's.
  feedback: MalnutritionFeedback | null;
  // Dispatches the submit and owns the toast + the close, so both survive this
  // panel unmounting. Resolves once the request settles, whichever way it went.
  onSubmit: (variables: MalnutritionFeedbackVariables) => Promise<void>;
  // Closes without writing anything.
  onSkip: () => void;
  // A save — the Yes/No that opened the panel, or this panel's own — hasn't
  // settled. Submit waits because both steps share one mutation, and a second
  // mutate() drops the first call's callbacks (the No's rollback and error).
  saveInFlight: boolean;
}

const CriteriaFeedbackPanel: FC<CriteriaFeedbackPanelProps> = ({
  emrPatientID,
  emrHospitalID,
  criteria,
  status,
  feedback,
  onSubmit,
  onSkip,
  saveInFlight,
}) => {
  // Everything the payload needs is frozen when the panel opens: the checklist
  // (so a background refetch can't mutate it under the user) and the patient it
  // was opened for (so a write in flight stays attributed to that patient).
  const [snapshot] = useState(() => ({
    emrPatientID,
    emrHospitalID,
    criteria: whyFlaggedCriteria(criteria, status),
  }));
  const [ids] = useState(() => {
    panelInstanceCount += 1;
    return {
      heading: `criteria-feedback-heading-${panelInstanceCount}`,
      hint: `criteria-feedback-hint-${panelInstanceCount}`,
    };
  });

  // Seeded once from the recorded answer, never from an effect. The card polls
  // every five minutes and every write invalidates the query, so re-seeding on
  // `feedback` would clear a half-typed comment under the user.
  //
  // Matched on the (fact, display_value) pair rather than `fact` alone: facts
  // can repeat — blank ones especially, and Why Flagged renders duplicates as
  // independent rows — so every row that matches lights up together. Identical
  // rows are indistinguishable on the wire, so restoring them together is the
  // honest reading rather than guessing which one was meant.
  const [checked, setChecked] = useState<boolean[]>(() => {
    const rejected = feedback?.rejected_criteria ?? [];
    return snapshot.criteria.map((criterion) =>
      rejected.some(
        (stored) =>
          stored.fact === criterion.fact &&
          stored.display_value === criterion.title
      )
    );
  });
  const [comment, setComment] = useState(() => feedback?.comment ?? "");
  // Latched from the seed too, or a restored comment already past the threshold
  // would show no counter until the user typed.
  const [counterShown, setCounterShown] = useState(
    () => (feedback?.comment?.length ?? 0) >= COMMENT_COUNTER_THRESHOLD
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Whether the panel opened on an answer that already had a reason recorded.
  // From the seed, not from `feedback`, so it tracks what the user is editing.
  const [hasStoredAnswer] = useState(
    () =>
      (feedback?.rejected_criteria?.length ?? 0) > 0 ||
      !!feedback?.comment?.trim()
  );

  const cardRef = useRef<HTMLDivElement | null>(null);
  const firstCheckboxRef = useRef<HTMLInputElement | null>(null);
  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const hasCriteria = snapshot.criteria.length > 0;

  // Focus lands on the first checkbox, or the comment field when there are none.
  useEffect(() => {
    (firstCheckboxRef.current ?? commentRef.current)?.focus();
  }, []);

  // If the panel opens below the fold, bring its heading to the top of the
  // visible area. (jsdom reports an all-zero rect, which reads as visible, so
  // this is inert in tests.)
  useEffect(() => {
    const node = cardRef.current;
    if (!node || typeof node.getBoundingClientRect !== "function") return;
    const { top } = node.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    if (top >= 0 && top <= viewportHeight) return;
    node.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, []);

  const trimmedComment = comment.trim();
  const hasContent = checked.some(Boolean) || trimmedComment.length > 0;
  // Compared against what was seeded, not against empty. Either signal on its
  // own is enough to submit a first answer, but clearing a recorded one is also
  // a legitimate edit — otherwise a user who decides none of the criteria were
  // wrong after all could only retract by answering Yes, which changes the
  // clinical answer as a side effect of wanting to take a reason back.
  const canSubmit = hasContent || hasStoredAnswer;
  const submitDisabled = !canSubmit || isSubmitting || saveInFlight;

  const handleToggle = (index: number) => () => {
    setChecked((previous) =>
      previous.map((value, idx) => (idx === index ? !value : value))
    );
  };

  const handleCommentChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // maxLength on the input already blocks typing past the cap and clips a
    // paste to the first 1,000 characters; this is belt and braces.
    const next = event.target.value.slice(0, MAX_COMMENT_LENGTH);
    setComment(next);
    if (next.length >= COMMENT_COUNTER_THRESHOLD) setCounterShown(true);
  };

  // Resends the No the panel was opened from, so a Submit stands on its own: it
  // records the answer even if the No click failed to save. Last writer wins,
  // as with the click itself.
  const buildVariables = (): MalnutritionFeedbackVariables => ({
    emrPatientID: snapshot.emrPatientID,
    emrHospitalID: snapshot.emrHospitalID,
    payload: {
      is_good_candidate: false,
      // Everything the panel offered, which is the denominator a rejection rate
      // is counted against — and the set the endpoint validates against, so it
      // has to be what was rendered rather than the raw criteria_met.
      presented_criteria: toWireCriteria(snapshot.criteria),
      rejected_criteria: toWireCriteria(
        snapshot.criteria.filter((_, idx) => checked[idx])
      ),
      comment: trimmedComment || null,
    },
  });

  const handleSubmit = () => {
    if (submitDisabled) return;
    setIsSubmitting(true);
    // No optimistic close: a lost multi-select isn't recoverable from the UI, so
    // the panel waits for the server before the parent tears it down.
    onSubmit(buildVariables()).then(() => {
      if (isMounted.current) setIsSubmitting(false);
    });
  };

  // Closes and writes nothing. It used to post an empty answer, which against a
  // shared row erased whatever a colleague had recorded — and there is no
  // "declined" state to store now, so leaving is just leaving. The button reads
  // "Skip" (per the design); that is still all it does, so don't wire the label
  // back to a write.
  const handleSkip = () => {
    if (isSubmitting) return;
    onSkip();
  };

  return (
    <Box
      ref={cardRef}
      className={styles.card}
      data-testid="criteria-feedback-panel"
    >
      <Box className={styles.headingRow}>
        <Typography id={ids.heading} sx={sxStyles.heading}>
          Which criteria didn’t apply here?
        </Typography>
        <Typography sx={sxStyles.subheading}>(Check all that apply)</Typography>
      </Box>

      {hasCriteria ? (
        <FormGroup
          role="group"
          aria-labelledby={ids.heading}
          sx={sxStyles.criteria}
        >
          {snapshot.criteria.map((criterion, index) => (
            <FormControlLabel
              key={index}
              sx={sxStyles.criterionRow}
              control={
                <Checkbox
                  size="small"
                  checked={checked[index]}
                  onChange={handleToggle(index)}
                  inputRef={index === 0 ? firstCheckboxRef : undefined}
                  sx={sxStyles.checkbox}
                />
              }
              label={criterion.title}
            />
          ))}
        </FormGroup>
      ) : (
        <Typography sx={sxStyles.emptyChecklist}>
          No criteria available for this patient.
        </Typography>
      )}

      <TextField
        multiline
        minRows={TEXTAREA_MIN_ROWS}
        maxRows={TEXTAREA_MAX_ROWS}
        variant="outlined"
        placeholder="(Other / additional context)"
        value={comment}
        onChange={handleCommentChange}
        inputRef={commentRef}
        inputProps={{
          maxLength: MAX_COMMENT_LENGTH,
          "aria-label": "Other / additional context",
        }}
        sx={sxStyles.comment}
      />
      {counterShown && (
        <Typography sx={sxStyles.counter}>
          {comment.length}/{MAX_COMMENT_LENGTH}
        </Typography>
      )}

      <Box className={styles.actions}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleSkip}
          disabled={isSubmitting}
          sx={{ ...sxStyles.buttonBase, ...sxStyles.skipButton }}
        >
          Skip
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={submitDisabled}
          aria-describedby={canSubmit ? undefined : ids.hint}
          sx={{ ...sxStyles.buttonBase, ...sxStyles.submitButton }}
        >
          Submit
        </Button>
      </Box>
      {/* Why Submit is disabled, exposed to assistive tech via aria-describedby
          above rather than only as a visual cue. */}
      <span id={ids.hint} className={styles.visuallyHidden}>
        {SUBMIT_HINT}
      </span>
    </Box>
  );
};

export default CriteriaFeedbackPanel;
